import { FC, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Controller, useFieldArray, useForm } from 'react-hook-form'

import {
	Alert,
	Box,
	Flashbar,
	FormField,
	Form,
	Button,
	Container,
	Grid,
	Header,
	Input,
	SpaceBetween
} from '@cloudscape-design/components'

import { useNewActiveDirectoryFormStore } from '../../../stores/active-directory/newActiveDirectoryFormStore'
import { useFlashbarStateStore } from '../../../stores/notifications/flashbarStateStore'
import useAddAdGroupMember from '../../../hooks/mutate/useAddAdGroupMember'
import {
	AddAdGroupMemberRequest,
	AdGroup,
	AdGroupMember
} from '../../../types/active-directory'
import useAllAdGroups from '../../../hooks/query/useAllAdGroups'
import FormSelect from '../../../components/ControlledFormInputs/Select'

interface ActiveDirectoryGroupOption {
	label: string
	value: string
	description?: string
	group: AdGroup
}

interface FormValues {
	adGroup: any
	members: AdGroupMember[]
}

const createEmptyMember = (): AdGroupMember => ({
	name: '',
	userId: '',
	email: ''
})

const STANDARD_USER_ID_PATTERN = /^\d{8,}$/
const VENDOR_USER_ID_PATTERN = /^(?:ms|zx|ve)\d{6}$/i
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
const USER_ID_VALIDATION_MESSAGE =
	'Enter at least 8 digits, or a vendor ID that starts with MS, ZX, or VE followed by 6 digits.'
const EMAIL_VALIDATION_MESSAGE =
	'Enter a valid email address in the format name@mail.com.'

const normalizeComparisonValue = (value: string): string =>
	value.trim().toLocaleLowerCase()

const isValidAdUserId = (value: string): boolean => {
	const trimmedValue = value.trim()
	return (
		STANDARD_USER_ID_PATTERN.test(trimmedValue) ||
		VENDOR_USER_ID_PATTERN.test(trimmedValue)
	)
}

const buildMembersPagePath = (groupName?: string): string =>
	groupName
		? `/active-directory/members?groupName=${encodeURIComponent(groupName)}`
		: '/active-directory'

const getDuplicateConflictSummary = (
	members: AdGroupMember[],
	selectedGroupMembers: AdGroupMember[]
): string | null => {
	const seenNames = new Set<string>()
	const seenUserIds = new Set<string>()
	const seenEmails = new Set<string>()
	const existingNames = new Set(
		selectedGroupMembers.map((member) => normalizeComparisonValue(member.name))
	)
	const existingUserIds = new Set(
		selectedGroupMembers.map((member) =>
			normalizeComparisonValue(member.userId)
		)
	)
	const existingEmails = new Set(
		selectedGroupMembers.map((member) =>
			normalizeComparisonValue(member.email || '')
		)
	)

	let hasDuplicateNameInRequest = false
	let hasDuplicateUserIdInRequest = false
	let hasDuplicateEmailInRequest = false
	let hasExistingNameConflict = false
	let hasExistingUserIdConflict = false
	let hasExistingEmailConflict = false

	members.forEach((member) => {
		const normalizedName = normalizeComparisonValue(member.name)
		const normalizedUserId = normalizeComparisonValue(member.userId)
		const normalizedEmail = normalizeComparisonValue(member.email || '')

		if (normalizedName) {
			if (seenNames.has(normalizedName)) {
				hasDuplicateNameInRequest = true
			}
			if (existingNames.has(normalizedName)) {
				hasExistingNameConflict = true
			}
			seenNames.add(normalizedName)
		}

		if (normalizedUserId) {
			if (seenUserIds.has(normalizedUserId)) {
				hasDuplicateUserIdInRequest = true
			}
			if (existingUserIds.has(normalizedUserId)) {
				hasExistingUserIdConflict = true
			}
			seenUserIds.add(normalizedUserId)
		}

		if (normalizedEmail) {
			if (seenEmails.has(normalizedEmail)) {
				hasDuplicateEmailInRequest = true
			}
			if (existingEmails.has(normalizedEmail)) {
				hasExistingEmailConflict = true
			}
			seenEmails.add(normalizedEmail)
		}
	})

	const messages: string[] = []

	if (
		hasDuplicateNameInRequest ||
		hasDuplicateUserIdInRequest ||
		hasDuplicateEmailInRequest
	) {
		messages.push(
			'Duplicate names, user IDs, or email addresses were found in this request. Please keep each member row unique.'
		)
	}

	if (
		hasExistingNameConflict ||
		hasExistingUserIdConflict ||
		hasExistingEmailConflict
	) {
		messages.push(
			'One or more names, user IDs, or email addresses already exist in the selected group.'
		)
	}

	return messages.length ? messages.join(' ') : null
}

const ACTIVE_DIRECTORY_FLASHBAR_PREFIX = 'active-directory:'

const renderMemberPromptContent = (
	groupName: string,
	members: AdGroupMember[],
	note?: string,
	errorMessage?: string
) => (
	<SpaceBetween size="xs">
		{note && <div>{note}</div>}
		<div>
			<strong>Group:</strong> {groupName}
		</div>
		<div>
			<strong>Users:</strong>
		</div>
		<ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
			{members.map((member) => (
				<li key={`${member.userId}-${member.name}`}>
					{member.name} ({member.userId}
					{member.email ? `, ${member.email}` : ''})
				</li>
			))}
		</ul>
		{errorMessage && (
			<div>
				<strong>Error:</strong> {errorMessage}
			</div>
		)}
	</SpaceBetween>
)

const NewAdGroupMemberForm: FC = () => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [memberConflictPrompt, setMemberConflictPrompt] = useState<
		string | null
	>(null)

	const { data: groups } = useAllAdGroups()
	const {
		items: flashbarItems,
		addItem: addFlashbarItem,
		removeItem: removeFlashbarItem
	} = useFlashbarStateStore()
	const activeDirectoryFlashbarItems = flashbarItems.filter((item) =>
		item.id.startsWith(ACTIVE_DIRECTORY_FLASHBAR_PREFIX)
	)

	const groupToOption = (group: AdGroup): ActiveDirectoryGroupOption => ({
		label: group.name,
		value: group.path,
		group: group
	})

	const {
		actions: { clearFormValues, setFormValues, setFormValidity }
	} = useNewActiveDirectoryFormStore()

	const {
		control,
		getValues,
		reset,
		setValue,
		watch,
		trigger,
		formState: { isValid }
	} = useForm<FormValues>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			adGroup: null,
			members: [createEmptyMember()]
		}
	})
	const { fields, append, remove, replace } = useFieldArray({
		control,
		name: 'members'
	})
	const requestedGroupName = searchParams.get('groupName') || ''
	const watchedAdGroup = watch('adGroup')
	const watchedMembers = watch('members')

	const validateDuplicateMemberField = (
		fieldName: keyof AdGroupMember,
		value: string,
		index: number
	) => {
		const normalizedValue = normalizeComparisonValue(value || '')
		if (!normalizedValue) {
			return true
		}

		const currentMembers = getValues('members') || []
		const duplicateInRequest = currentMembers.some((member, memberIndex) => {
			if (memberIndex === index) {
				return false
			}

			return (
				normalizeComparisonValue(member[fieldName] || '') === normalizedValue
			)
		})

		if (duplicateInRequest) {
			const labels: Record<keyof AdGroupMember, string> = {
				name: 'name',
				userId: 'user ID',
				email: 'email address'
			}
			return `This ${labels[fieldName]} is duplicated in this request`
		}

		const selectedGroupMembers =
			(getValues('adGroup') as ActiveDirectoryGroupOption | null)?.group
				?.members || []

		const existsInSelectedGroup = selectedGroupMembers.some(
			(member) =>
				normalizeComparisonValue(member[fieldName] || '') === normalizedValue
		)

		if (existsInSelectedGroup) {
			const labels: Record<keyof AdGroupMember, string> = {
				name: 'name',
				userId: 'user ID',
				email: 'email address'
			}
			return `This ${labels[fieldName]} already exists in the selected group`
		}

		return true
	}

	const validateMemberUserIdField = (value: string, index: number) => {
		const trimmedValue = value.trim()
		if (!trimmedValue) {
			return 'Please enter a user ID'
		}

		if (!isValidAdUserId(trimmedValue)) {
			return USER_ID_VALIDATION_MESSAGE
		}

		return validateDuplicateMemberField('userId', trimmedValue, index)
	}

	const validateMemberEmailField = (value: string | undefined, index: number) => {
		const trimmedValue = (value || '').trim()
		if (!trimmedValue) {
			return 'Please enter an email address'
		}

		if (!EMAIL_PATTERN.test(trimmedValue)) {
			return EMAIL_VALIDATION_MESSAGE
		}

		return validateDuplicateMemberField('email', trimmedValue, index)
	}

	useEffect(() => {
		setFormValidity(isValid)
	}, [isValid, setFormValidity])

	useEffect(() => {
		clearFormValues()
		setFormValidity(false)
	}, [clearFormValues, setFormValidity])

	useEffect(() => {
		setMemberConflictPrompt(null)
	}, [watchedAdGroup, watchedMembers])

	useEffect(() => {
		if (!requestedGroupName || !groups?.length) {
			return
		}

		const currentGroupName = watchedAdGroup?.label || ''
		if (
			normalizeComparisonValue(currentGroupName) ===
			normalizeComparisonValue(requestedGroupName)
		) {
			return
		}

		const requestedGroup = groups.find(
			(group) =>
				normalizeComparisonValue(group.name) ===
				normalizeComparisonValue(requestedGroupName)
		)

		if (!requestedGroup) {
			return
		}

		const selectedGroupOption = groupToOption(requestedGroup)
		setValue('adGroup', selectedGroupOption, {
			shouldDirty: false,
			shouldValidate: true
		})
		setFormValues('adGroup', selectedGroupOption)
	}, [groups, requestedGroupName, setFormValues, setValue, watchedAdGroup])

	const { mutate: addAdGroupMember, isLoading } = useAddAdGroupMember()

	const addActiveDirectoryFlashbar = ({
		type,
		header,
		content
	}: {
		type: 'success' | 'error'
		header: string
		content: JSX.Element | string
	}) => {
		activeDirectoryFlashbarItems.forEach((item) => removeFlashbarItem(item.id))

		const id = `${ACTIVE_DIRECTORY_FLASHBAR_PREFIX}${Date.now()}`
		addFlashbarItem({
			type,
			dismissible: true,
			dismissLabel: `Dismiss ${type}`,
			id,
			header,
			content,
			onDismiss: () => removeFlashbarItem(id)
		})
	}

	const resetFormState = () => {
		const resetMembers = [createEmptyMember()]

		setMemberConflictPrompt(null)
		clearFormValues()
		setFormValidity(false)
		replace(resetMembers)
		reset({
			adGroup: null,
			members: resetMembers
		})
	}

	const handleAddMemberRow = () => {
		append(createEmptyMember())
	}

	const handleRemoveMemberRow = (index: number) => {
		if (fields.length === 1) return

		remove(index)
	}

	const handleSubmit = async () => {
		const valid = await trigger()

		const currentFormValues = getValues()
		const selectedGroupMembers =
			(currentFormValues.adGroup as ActiveDirectoryGroupOption | null)?.group
				?.members || []
		const duplicateConflictSummary = getDuplicateConflictSummary(
			currentFormValues.members,
			selectedGroupMembers
		)

		if (!valid) {
			setMemberConflictPrompt(duplicateConflictSummary)
			return
		}

		setMemberConflictPrompt(null)

		const members = currentFormValues.members.map((member) => ({
			name: member.name.trim(),
			userId: member.userId.trim(),
			email: (member.email || '').trim()
		}))

		if (!members.length) return

		const addAdGroupMemberRequest: AddAdGroupMemberRequest = {
			groupName: currentFormValues.adGroup.label,
			members
		}

		addAdGroupMember(addAdGroupMemberRequest, {
			onSuccess: () => {
				addActiveDirectoryFlashbar({
					type: 'success',
					header: `Submitted request to add ${members.length} member${
						members.length === 1 ? '' : 's'
					} to ${currentFormValues.adGroup.label}`,
					content: renderMemberPromptContent(
						currentFormValues.adGroup.label,
						members,
						"The request was created and is being processed. Check your request's progress in the Merge Requests tab in the Active-Directory Repository.",
						undefined
					)
				})
				resetFormState()
				navigate(buildMembersPagePath(currentFormValues.adGroup?.label), {
					replace: true
				})
			},
			onError: (error) => {
				const errorMessage =
					error instanceof Error
						? error.message
						: 'Failed to add Active Directory group members'

				addActiveDirectoryFlashbar({
					type: 'error',
					header: `Failed to add members to ${currentFormValues.adGroup.label}`,
					content: renderMemberPromptContent(
						currentFormValues.adGroup.label,
						members,
						undefined,
						errorMessage
					)
				})
			}
		})
	}

	const handleCancel = () => {
		const currentFormValues = getValues()
		resetFormState()
		navigate(buildMembersPagePath(currentFormValues.adGroup?.label), {
			replace: true
		})
	}

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="link" onClick={handleCancel}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSubmit} loading={isLoading}>
						Submit
					</Button>
				</SpaceBetween>
			}
			header={
				<Header variant="h1" description="">
					Add Active Directory Group Members
				</Header>
			}
		>
			<Container>
				<SpaceBetween direction="vertical" size="l">
					{activeDirectoryFlashbarItems.length > 0 && (
						<Flashbar items={activeDirectoryFlashbarItems} />
					)}

					{memberConflictPrompt && (
						<Alert
							type="error"
							header="Duplicate member details found"
							statusIconAriaLabel="Error"
						>
							{memberConflictPrompt}
						</Alert>
					)}

					<FormSelect
						options={(groups || []).map(groupToOption)}
						placeholder=""
						name="adGroup"
						label="AD Group"
						control={control as any}
						rules={{
							required:
								'Please enter the name of the active directory group you want to add the user to'
						}}
						setFormValues={setFormValues}
					/>

					<SpaceBetween size="m">
						<Box variant="awsui-key-label">Members</Box>
						<Box color="text-body-secondary">
							Add one or more users. Each row needs a display name, user ID, and email address.
						</Box>

						{fields.map((field, index) => (
							<Grid
								key={field.id}
								gridDefinition={[
									{ colspan: 4 },
									{ colspan: 3 },
									{ colspan: 4 },
									{ colspan: 1 }
								]}
							>
								<Controller
									name={`members.${index}.name` as const}
									control={control}
									rules={{
										required: 'Please enter a member name',
										validate: (value) =>
											validateDuplicateMemberField('name', value, index)
									}}
									render={({ field, fieldState }) => (
										<FormField
											label="Name"
											errorText={fieldState.error?.message}
										>
											<Input
												value={field.value}
												placeholder="Jane Doe"
												onChange={({ detail }) => {
													field.onChange(detail.value)
												}}
											/>
										</FormField>
									)}
								/>

								<Controller
									name={`members.${index}.userId` as const}
									control={control}
									rules={{
										required: 'Please enter a user ID',
										validate: (value) => validateMemberUserIdField(value, index)
									}}
									render={({ field, fieldState }) => (
										<FormField
											label="User ID"
											errorText={fieldState.error?.message}
										>
											<Input
												value={field.value}
												placeholder="8 digit network ID or vendor ID (ms-, ve- or zx- followed by 6 digits)"
												onChange={({ detail }) => {
													field.onChange(detail.value)
												}}
											/>
										</FormField>
									)}
								/>

								<Controller
									name={`members.${index}.email` as const}
									control={control}
									rules={{
										required: 'Please enter an email address',
										validate: (value) => validateMemberEmailField(value, index)
									}}
									render={({ field, fieldState }) => (
										<FormField
											label="Email"
											errorText={fieldState.error?.message}
										>
											<Input
												value={field.value || ''}
												placeholder="name@mail.com"
												onChange={({ detail }) => {
													field.onChange(detail.value)
												}}
											/>
										</FormField>
									)}
								/>

								<Box padding={{ top: 'l' }}>
									<Button
										variant="link"
										iconName="remove"
										ariaLabel={`Remove member row ${index + 1}`}
										disabled={fields.length === 1}
										onClick={() => handleRemoveMemberRow(index)}
									/>
								</Box>
							</Grid>
						))}

						<Button iconName="add-plus" onClick={handleAddMemberRow}>
							Add member
						</Button>
					</SpaceBetween>
				</SpaceBetween>
			</Container>
		</Form>
	)
}

export default NewAdGroupMemberForm
